import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PageForm from 'components/pageForm/PageForm';
import { apiErrorText } from 'helper/apiErrorText';
import { clientSizeLabel } from 'constants/clientSizes';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

// As the api takes them (settings/client-sizes/update, visits): whole numbers
// from 1 to `max`. `size` is the client size the row sets the visits of.
const VISIT_FIELDS = [
  { key: 'bigPerMonth', max: 8, size: 'grand_compte' },
  { key: 'midEveryWeeks', max: 26, size: 'pme' },
  { key: 'smallEveryMonths', max: 12, size: 'tpe' },
  { key: 'placeWithinDays', max: 60 },
];
const isInRange = (value, max) =>
  /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= max;
const draftOf = (visits) =>
  Object.fromEntries(
    VISIT_FIELDS.map(({ key }) => [key, String(visits[key])]),
  );

/**
 * « Paramètres · Rendez-vous clients » of the admin console: how often the
 * planning proposes a follow-up visit to each client, by its size, and the
 * days given to place an automatic visit. Read and saved by the api
 * (admin/crm/settings/client-sizes, `visits` only: the size thresholds are
 * left as they are).
 */
const AdminAppointments = () => {
  const { text } = useText('adminConsole');
  const { language } = UseContext();
  const { call, ready } = useCrmContext();
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    call(crmApi.getClientSizeSettings).then(
      ({ visits }) => {
        setSaved(visits);
        setDraft(draftOf(visits));
      },
      () => setFailed(true),
    );
  }, [call]);
  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  const changed =
    saved &&
    draft &&
    VISIT_FIELDS.some(({ key }) => draft[key] !== String(saved[key]));
  const problem =
    draft && !VISIT_FIELDS.every(({ key, max }) => isInRange(draft[key], max))
      ? text('visitsInvalid')
      : null;

  const save = () => {
    setSaving(true);
    call(crmApi.saveClientSizeSettings, {
      visits: Object.fromEntries(
        VISIT_FIELDS.map(({ key }) => [key, Number(draft[key])]),
      ),
    }).then(
      // The values shown are the api's, as it kept them.
      ({ visits }) => {
        setSaving(false);
        setSaved(visits);
        setDraft(draftOf(visits));
        toast.success(text('visitsSaved'));
      },
      (error) => {
        setSaving(false);
        toast.error(apiErrorText(error, { language }));
      },
    );
  };

  const labelOf = ({ key, size }) =>
    size ? clientSizeLabel(language, size) : text(`visitsLabel_${key}`);

  const field = (row) => (
    <span className="flex items-center gap-2">
      {text(`visitsBefore_${row.key}`)}
      <input
        type="text"
        inputMode="numeric"
        aria-label={text('visitsInputLabel', { label: labelOf(row) })}
        value={draft[row.key]}
        onChange={(event) =>
          setDraft((current) => ({ ...current, [row.key]: event.target.value }))
        }
        className={`h-9 w-20 rounded-md border bg-white px-3 text-right text-sm text-gray-900 focus:outline-none ${isInRange(draft[row.key], row.max) ? 'border-gray-200 focus:border-yellow-500' : 'border-red-500'}`}
      />
      {text(`visitsAfter_${row.key}`)}
    </span>
  );

  return (
    <PageForm title={text('visitsPageTitle')}>
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 px-4 py-6 lg:px-8">
        {failed && (
          <CompactEmptyState
            title={text('visitsLoadError')}
            action={{ label: text('goalsRetry'), onClick: load }}
          />
        )}
        {!failed && !draft && (
          <div
            className="h-40 animate-pulse rounded-lg bg-gray-100"
            aria-busy="true"
          />
        )}
        {!failed && draft && (
          <section
            aria-labelledby="visits-title"
            className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white"
          >
            <div className="flex flex-col gap-0.5 px-4 py-4">
              <h2
                id="visits-title"
                className="text-base font-semibold text-gray-900"
              >
                {text('visitsTitle')}
              </h2>
              <p className="text-sm text-gray-500">{text('visitsText')}</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-gray-100">
                  <th className="bg-gray-50 py-3.5 pl-4 pr-2 text-left font-medium text-gray-700">
                    {text('sizesSize')}
                  </th>
                  <th className="bg-gray-50 py-3.5 pl-2 pr-4 text-right font-medium text-gray-700">
                    {text('sizesRule')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {VISIT_FIELDS.map((row) => (
                  <tr key={row.key} className="border-b border-gray-50">
                    <td className="py-3 pl-4 pr-2 text-gray-900">
                      {labelOf(row)}
                    </td>
                    <td className="flex justify-end py-3 pl-2 pr-4 text-gray-600">
                      {field(row)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
              {text('visitsHow')}
            </p>
            {changed && (
              <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-4 py-3">
                <span className="mr-auto text-xs text-gray-500">
                  {problem ?? text('goalsUnsaved')}
                </span>
                <Button
                  color="SECONDARY"
                  size="SM"
                  onClick={() => setDraft(draftOf(saved))}
                >
                  {text('goalsCancel')}
                </Button>
                <Button
                  color="PRIMARY"
                  size="SM"
                  disabled={Boolean(problem) || saving}
                  onClick={save}
                >
                  {text('goalsSave')}
                </Button>
              </div>
            )}
          </section>
        )}
      </div>
    </PageForm>
  );
};

export default AdminAppointments;
